import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { DollarSign, Users, TrendingUp, Clock, Search, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminCreditPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('total_credit_allocated');
  const [sortDir, setSortDir] = useState('desc');

  const loadReport = async () => {
    try {
      const { data } = await adminAPI.getCreditReport();
      setReport(data);
    } catch (err) {
      toast.error('Failed to load credit report');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadReport(); }, []);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filteredUsers = (report?.users || [])
    .filter(u => u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()) || u.user_id?.includes(search))
    .sort((a, b) => {
      const av = a[sortKey] || 0, bv = b[sortKey] || 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const s = report?.summary || {};

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-credit-page">
        <div>
          <h2 className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)]">Credit Usage Report</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">Track admin trade credit allocation and consumption across all users</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="credit-summary">
              <SummaryCard icon={DollarSign} label="Total Allocated" value={fmt(s.total_credit_allocated)} color="green" testId="total-allocated" />
              <SummaryCard icon={TrendingUp} label="Used in Trades" value={fmt(s.total_credit_used_in_trades)} color="amber" testId="total-used" />
              <SummaryCard icon={Clock} label="In Open Trades" value={fmt(s.total_open_credit_in_use)} color="blue" testId="open-credit" />
              <SummaryCard icon={DollarSign} label="Remaining" value={fmt(s.total_credit_remaining)} color="emerald" testId="total-remaining" />
              <SummaryCard icon={Users} label="Users with Credit" value={s.users_with_credit || 0} color="purple" testId="users-with-credit" />
            </div>

            {/* Per-User Table */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid="credit-users-table">
              <div className="px-5 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] whitespace-nowrap">Per-User Breakdown</h3>
                <div className="relative max-w-xs w-full">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
                  <input
                    type="text" placeholder="Search users..."
                    value={search} onChange={e => setSearch(e.target.value)}
                    data-testid="credit-search"
                    className="w-full pl-9 pr-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500/50"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] text-[var(--text-tertiary)] uppercase border-b border-[var(--border-subtle)]">
                      <th className="text-left px-5 py-2.5">User</th>
                      <SortHeader label="Wallet" sortKey="wallet_balance" current={sortKey} dir={sortDir} toggle={toggleSort} />
                      <SortHeader label="Allocated" sortKey="total_credit_allocated" current={sortKey} dir={sortDir} toggle={toggleSort} />
                      <SortHeader label="Remaining" sortKey="credit_remaining" current={sortKey} dir={sortDir} toggle={toggleSort} />
                      <SortHeader label="Used in Trades" sortKey="credit_used_in_trades" current={sortKey} dir={sortDir} toggle={toggleSort} />
                      <SortHeader label="In Open Trades" sortKey="open_credit_in_use" current={sortKey} dir={sortDir} toggle={toggleSort} />
                      <th className="text-right px-3 py-2.5">Trades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)] transition-colors" data-testid={`credit-row-${u.user_id}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-bold text-green-400">{u.name?.[0]?.toUpperCase() || '?'}</span>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-[var(--text-primary)]">{u.name}</p>
                              <p className="text-[10px] text-[var(--text-tertiary)]">{u.email} <span className="font-mono">#{u.user_id}</span></p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{fmt(u.wallet_balance)}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-green-400">{fmt(u.total_credit_allocated)}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-emerald-400">{fmt(u.credit_remaining)}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-amber-400">{fmt(u.credit_used_in_trades)}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs text-blue-400">{fmt(u.open_credit_in_use)}</td>
                        <td className="px-3 py-3 text-right text-xs text-[var(--text-secondary)]">
                          <span className="font-mono">{u.trade_count_with_credit}</span>
                          {u.open_trades_with_credit > 0 && (
                            <span className="text-[10px] text-blue-400 ml-1">({u.open_trades_with_credit} open)</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-xs text-[var(--text-tertiary)]">No users with credit activity</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Audit Log */}
            {report?.recent_audit_log?.length > 0 && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid="credit-audit-log">
                <div className="px-5 py-3 border-b border-[var(--border-subtle)]">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Recent Credit Changes</h3>
                </div>
                <div className="divide-y divide-[var(--border-subtle)] max-h-[300px] overflow-y-auto">
                  {report.recent_audit_log.map((log, i) => (
                    <div key={i} className="px-5 py-2.5 flex items-center justify-between text-xs" data-testid={`audit-log-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                        <span className="text-[var(--text-secondary)]">
                          Admin <span className="font-mono text-[var(--text-primary)]">#{log.admin_id}</span> set credit for user <span className="font-mono text-[var(--text-primary)]">#{log.target_user_id}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono text-green-400">
                          {log.details?.old_credit != null
                            ? `${fmt(log.details.old_credit)} → ${fmt(log.details.new_credit)}`
                            : fmt(log.details?.admin_trade_credit || log.details?.new_credit)
                          }
                        </span>
                        <span className="text-[var(--text-tertiary)] text-[10px]">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, color, testId }) {
  const colorMap = {
    green: 'bg-green-500/10 text-green-400 border-green-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };
  return (
    <div className={`p-4 rounded-lg border ${colorMap[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</span>
      </div>
      <p className="text-lg font-bold font-mono">{value}</p>
    </div>
  );
}

function SortHeader({ label, sortKey, current, dir, toggle }) {
  const active = current === sortKey;
  return (
    <th className="text-right px-3 py-2.5 cursor-pointer select-none hover:text-[var(--text-primary)] transition-colors" onClick={() => toggle(sortKey)}>
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? 'text-green-400' : 'opacity-40'}`} />
      </span>
    </th>
  );
}
